import { isGoogleCloudStorageUrl } from "mt-commands/helpers/isGoogleCloudStorageUrl";
import type { VideoHTMLAttributes } from "react";
import { useContext } from "react";
import { useAfterFirstScroll } from "../../../hooks/useAfterFirstScroll";
import { useAfterLoadDelay } from "../../../hooks/useAfterLoadDelay";
import { CloudinaryContext } from "./Provider";
import { CloudinaryTransformationParamKey } from "./constants";

const cloudinaryVideoDefaultTransformations = [
  "q_auto",
  "f_auto",
  "dpr_2.0",
  "c_scale",
];

type CloudinaryVideoTypeNoSrc = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  "src"
>;

interface CloudinaryVideoProps extends CloudinaryVideoTypeNoSrc {
  transformations?: Array<CloudinaryTransformationParamKey>;
  src?: { type?: string; url?: string; formats?: [any] };
  videoRef?: any;
  // Start loading the video this long after the window's load event. Until then the
  // <video> has no source, so it keeps its size but downloads nothing.
  loadDelayMs?: number;
  // Start loading the video once the visitor scrolls the window.
  loadOnScroll?: boolean;
}

export function CloudinaryVideo({
  src,
  transformations,
  videoRef,
  loadDelayMs,
  loadOnScroll = false,
  ...props
}: CloudinaryVideoProps) {
  const isDelayOver = useAfterLoadDelay(loadDelayMs);
  const hasScrolled = useAfterFirstScroll(loadOnScroll);
  const canLoad = isDelayOver && hasScrolled;
  const { cloudName, autoUploadMappingFolder } = useContext(CloudinaryContext);
  const stringifiedParams = cloudinaryVideoDefaultTransformations
    .concat(transformations || [])
    .join(",");

  const cloudinaryStagingRoot = `https://res.cloudinary.com/${cloudName}/video/upload/${stringifiedParams}/v1/${autoUploadMappingFolder[0]?.folder}/`;
  const cloudinaryProductionRoot = `https://res.cloudinary.com/${cloudName}/video/upload/${stringifiedParams}/v1/${autoUploadMappingFolder[0]?.folder}/`;

  let url =
    src?.url && isGoogleCloudStorageUrl(src.url)
      ? `https://res.cloudinary.com/${cloudName}/video/fetch/${stringifiedParams}/${src.url}`
      : src?.url
          ?.replace(
            `${autoUploadMappingFolder[0]?.urlPrefix}`,
            cloudinaryStagingRoot,
          )
          ?.replace(
            `${autoUploadMappingFolder[0]?.urlPrefix}`,
            cloudinaryProductionRoot,
          );

  if (!url || !url.includes("cloudinary")) {
    const appendRoot =
      `https://res.cloudinary.com/${cloudName}/video/upload/${stringifiedParams}/v1/` +
      url;
    url = appendRoot;
  }

  if (
    url &&
    !url.includes(stringifiedParams) &&
    !url.includes("/v1/s3bucket/videos/")
  ) {
    url = url.replace(
      `https://res.cloudinary.com/${cloudName}/video/upload/`,
      `https://res.cloudinary.com/${cloudName}/video/upload/${stringifiedParams}/`,
    );
  }

  return (
    <video
      {...props}
      ref={videoRef}
      // Remounts once the source is added, so autoPlay applies to it.
      key={canLoad ? url : "pending"}
      controls
      controlsList="nodownload"
    >
      {canLoad && <source src={`${url}#t=0.1`} type="video/mp4" />}
    </video>
  );
}

export default CloudinaryVideo;
